import React, { Fragment, useCallback } from "react";
import clsx from "clsx";
import { useForm, useFormState } from "react-final-form";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import Autocomplete from "../../../../components/Autocomplete";
import Field from "../../../../components/Field";
import { mutateErrors } from "../../../../utils";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { useNotify } from "../../../notify/useNotify";
import { useApplicationQueries } from "../../hooks/useApplicationQueries";
import { editContentConfig } from "./editContentConfig";
import WeduCheckbox from "../../../../components/WeduCheckbox/WeduCheckbox";
import { WeduLabel } from "../../../../components/WeduLabel/WeduLabel";
import { EducationType } from "../../../school/constants/types";

const useStyles = makeStyles(() => ({
  card: {
    padding: "0px 11px 16px 16px",
    color: "#000",
    fontWeight: "normal",
    fontSize: 16,
    minHeight: 88,
    margin: -16,
  },
  label: {
    color: "#383d4a",
    fontWeight: "bold",
    fontSize: 18,
    display: "flex",
    "& > span": {
      paddingTop: 16,
      paddingBottom: 8,
    },
  },
  weduLabel: {
    color: "#383d4a",
    fontWeight: "bold",
    fontSize: 18,
    marginTop: 16,
    marginBottom: 8,
  },
  uploadFileContainer: {
    "& > div > div > div": {
      padding: 20,
      border: "solid 1px #dbdbdb",
      borderRadius: 12,
    },
  },
  uploadFilePreview: {
    "& > div > div > div": {
      padding: 20,
    },
  },
  sectionContainer: {
    marginTop: 22,
    marginBottom: 16,
  },
  titleContainer: {
    fontWeight: "bold",
    margin: 0,
    marginBottom: 8,

    "& p": {
      margin: 0,
    },
  },
  link: {
    fontSize: 16,
    color: "#7D9EF1",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "none",
      color: "#5E88F1",
    },
  },
  checkboxContent: {
    "&>label": {
      marginLeft: -8,
    },
  },
  reqDocTitle: {
    "& ::first-letter": {
      textTransform: "uppercase",
    },
  },
  uploadErrorContainer: {
    fontSize: 12,
    fontWeight: "bold",
    marginLeft: "auto",
    color: "#F94B40",
    minHeight: 18,
  },
  uploaderHasError: {
    color: "#F94B40",
  },
  hasError: {
    color: "#F94B40",
    fontWeight: "bold",
    width: 125,
  },
  commentContainer: {
    padding: 32,
    marginRight: 5,
    borderRadius: 12,
    border: "solid 0.5px #dbdbdb",
    minHeight: 82,
  },
}));

enum DocType {
  ApplicationFile = 1,
  RequiredDoc = 2,
  ExtraDoc = 3,
}

const EditApplication = () => {
  const classes = useStyles();

  const { values } = useFormState();
  const snackNotify = useNotify();
  const { submitErrors } = useFormState();
  const {
    mutators: { push, remove },
    change,
  } = useForm();
  const { uploadApplicationForm, uploadRequiredDocs, uploadExtraDocs } =
    useApplicationQueries();

  const stepName = editContentConfig.stepName!;
  const linkConfirmationErrorText = submitErrors?.[stepName]?.isLinkConfirmed;
  const isLinkConfirmed = values[stepName]?.isLinkConfirmed || false;
  const educationType = values.injectedData.educationType;
  const applicationForms = values.injectedData.filledForms;
  const applicationLink =
    educationType === EducationType.Language
      ? values.injectedData.course.applicationLink
      : values.injectedData.program.academic.applicationLink;
  const requiredDocs = values.injectedData.requiredDocs;
  const addedExtraDocs = values[stepName]?.extraDocs
    ? values[stepName]?.extraDocs
    : [];
  const extraDocs = [...values.injectedData.extraDocs, ...addedExtraDocs];
  const hasApplicationFileError =
    Object.keys(submitErrors?.[stepName]?.applicationFiles || {}).length > 0;

  const uploadFile = useCallback(
    (file, docType: DocType) => {
      switch (docType) {
        case DocType.ApplicationFile:
          return uploadApplicationForm(file);
        case DocType.RequiredDoc:
          return uploadRequiredDocs(file);
        case DocType.ExtraDoc:
          return uploadExtraDocs(file);
        default:
          throw new Error("docType not found!");
      }
    },
    [uploadApplicationForm, uploadExtraDocs, uploadRequiredDocs],
  );

  const addNewFiles = useCallback(
    async (files, docType: DocType, docId?) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadFile(file, docType);
          switch (docType) {
            case DocType.ApplicationFile:
              change(`${stepName}.applicationFiles.${docId}`, data);
              break;
            case DocType.RequiredDoc:
              change(`${stepName}.requiredDocs.${docId}`, data);
              break;
            case DocType.ExtraDoc:
              push(`${stepName}.extraDocs`, data);
              break;
            default:
              console.error("docType not found!");
          }
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);

          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [change, push, snackNotify, stepName, uploadFile],
  );

  const removeFileHandler = useCallback(
    (file, docType: DocType, docId?) => {
      if (docType === DocType.RequiredDoc) {
        return change(`${stepName}.requiredDocs.${docId}`, undefined);
      }
      if (docType === DocType.ExtraDoc) {
        let index = values.injectedData.extraDocs?.indexOf(file);
        return remove(`${stepName}.extraDocs`, index);
      }
      if (docType === DocType.ApplicationFile) {
        return change(`${stepName}.applicationFiles.${docId}`, undefined);
      }
    },
    [change, remove, stepName, values.injectedData.extraDocs],
  );

  return (
    <>
      <div className={classes.card}>
        <div className={classes.label}>
          <span>Student</span>
        </div>
        {values.injectedData.student.name}
      </div>
      <div className={classes.card}>
        <div className={classes.label}>
          <span>Branch</span>
        </div>
        {values.injectedData.branch.name}
      </div>
      {(educationType === EducationType.Higher ||
        educationType === EducationType.Secondary) && (
        <div className={classes.card}>
          <div className={classes.label}>
            <span>Intake</span>
          </div>
          {new Date(values.selectedIntake.deadline) <= new Date() ? (
            <Field
              fullWidth
              required
              disableClearable
              name={`${stepName}.intake`}
              placeholder="Please select the intake"
              options={values.injectedData.program.intakes}
              component={Autocomplete}
            />
          ) : (
            <span>{values.selectedIntake.name}</span>
          )}
        </div>
      )}
      {!!applicationForms.length && (
        <div className={classes.card}>
          <WeduLabel
            label="Application Files"
            labelStyles={classes.weduLabel}
            isRequired
            hasError={hasApplicationFileError}
          />
          <Grid container spacing={2}>
            {applicationForms.map((appForm) => {
              const hasError =
                submitErrors?.[stepName]?.applicationFiles?.[
                  appForm.received.id
                ];

              return (
                <Fragment key={appForm.received.id}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    className={classes.uploadFileContainer}
                  >
                    <UploadFile
                      isViewMode
                      uploadedFiles={appForm.received ? [appForm.received] : []}
                    />
                  </Grid>
                  {appForm.filled ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      className={classes.uploadFileContainer}
                    >
                      <UploadFile
                        isViewMode
                        uploadedFiles={
                          appForm.filled ? [appForm.filled.file] : []
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      className={clsx(
                        !appForm.filled && classes.uploadFilePreview,
                      )}
                    >
                      <UploadFile
                        required
                        uploadedFiles={
                          values[stepName]?.applicationFiles?.[
                            appForm.received.id
                          ]
                            ? [
                                values[stepName]?.applicationFiles?.[
                                  appForm.received.id
                                ],
                              ]
                            : []
                        }
                        addNewFiles={(files) =>
                          addNewFiles(
                            files,
                            DocType.ApplicationFile,
                            appForm.received.id,
                          )
                        }
                        removeFile={(file) =>
                          removeFileHandler(
                            file,
                            DocType.ApplicationFile,
                            appForm.received.id,
                          )
                        }
                        infoText={
                          "You can browse hard copy of application form or/and any docs required."
                        }
                        allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                        maxSize={30}
                      />
                      <Typography className={classes.uploadErrorContainer}>
                        {hasError}
                      </Typography>
                    </Grid>
                  )}
                </Fragment>
              );
            })}
          </Grid>
        </div>
      )}
      {!!applicationLink && (
        <div className={classes.sectionContainer}>
          <div className={classes.titleContainer}>
            <p>Application Link *</p>
          </div>
          <Link href={applicationLink} className={classes.link} target="_blank">
            Get Link Here
          </Link>
          <div className={classes.checkboxContent}>
            <Field
              type="checkbox"
              name={`${stepName}.isLinkConfirmed`}
              hasError={!!linkConfirmationErrorText && !isLinkConfirmed}
              label={
                "Please fill out all the necessary details and tick the checkbox"
              }
              component={WeduCheckbox}
            />
          </div>
        </div>
      )}
      {!!requiredDocs.length && (
        <div className={classes.card}>
          <div className={classes.label}>
            <span>Required Documents</span>
          </div>
          <Grid container spacing={2}>
            {requiredDocs.map((reqDoc) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={reqDoc.received.id}
                className={clsx(
                  !!reqDoc.filled && classes.uploadFileContainer,
                  !reqDoc.filled && classes.uploadFilePreview,
                )}
              >
                <span className={classes.reqDocTitle}>
                  {reqDoc.received.name}
                </span>
                {!!reqDoc.filled ? (
                  <UploadFile
                    isViewMode
                    uploadedFiles={reqDoc.filled ? [reqDoc.filled.file] : []}
                  />
                ) : (
                  <UploadFile
                    uploadedFiles={
                      values[stepName]?.requiredDocs?.[reqDoc.received.id]
                        ? [values[stepName]?.requiredDocs?.[reqDoc.received.id]]
                        : []
                    }
                    addNewFiles={(files) =>
                      addNewFiles(
                        files,
                        DocType.RequiredDoc,
                        reqDoc.received.id,
                      )
                    }
                    removeFile={(file) =>
                      removeFileHandler(
                        file,
                        DocType.RequiredDoc,
                        reqDoc.received.id,
                      )
                    }
                    infoText={
                      "You can browse hard copy of application form or/and any docs required."
                    }
                    allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                    maxSize={30}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </div>
      )}
      <div className={classes.sectionContainer}>
        <div className={classes.titleContainer}>
          <p>Upload any document that will support your application</p>
        </div>
        <UploadFile
          uploadedFiles={extraDocs || []}
          addNewFiles={(files) => addNewFiles(files, DocType.ExtraDoc)}
          removeFile={(file) => removeFileHandler(file, DocType.ExtraDoc)}
          infoText={
            "You can browse hard copy of application form or/and any docs required."
          }
          allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
          maxSize={30}
          maxFiles={10}
        />
      </div>
      {!!values.injectedData.comment && (
        <div className={classes.card}>
          <div className={classes.label}>
            <span>Comment</span>
          </div>
          <div className={classes.commentContainer}>
            {values.injectedData.comment}
          </div>
        </div>
      )}
    </>
  );
};

export default EditApplication;
